<!--
	ArticlePage — the frame for buying-question pages (compatibility guide,
	comparisons). Same rhythm as the product page: a dark hero with the words over
	a real render and the route printed as readout lines under it, the page's own Sections in
	between, and the same ending every time: the workflow demo beside the presale.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import Nav from './Nav.svelte';
	import Footer from './Footer.svelte';
	import { Button, Reveal, Section } from '$lib/components/ui';
	import { demo } from '$lib/content/demo';
	import { pricingCard } from '$lib/content/pricing';
	import { page } from '$app/state';
	import { articleJsonLd, jsonLdScriptTag } from '$lib/seo/jsonld';

	let {
		label,
		title,
		lede,
		updated,
		image,
		routes,
		children
	}: {
		label: string;
		title: string;
		lede: string;
		/** ISO date the facts on the page were last checked. */
		updated: string;
		/** A real render from a real job. Never an illustration. */
		image: { asset: string; w: number; h: number; alt: string; caption: string };
		/** RouteLine rows printed under the scene. */
		routes: Snippet;
		children: Snippet;
	} = $props();
</script>

<svelte:head>
	<!-- eslint-disable-next-line svelte/no-at-html-tags -- build-time-only JSON-LD from first-party content modules, never user input -->
	{@html jsonLdScriptTag(
		articleJsonLd({
			path: page.url.pathname,
			headline: title,
			description: lede,
			dateModified: updated,
			image: `/img/${image.asset}.webp`
		})
	)}
</svelte:head>

<Nav tone="dark" />
<main>
	<Section as="header" tone="dark" grid class="ap-hero">
		<div class="ap-scene">
			<!-- Same staging as the product page: the real render floats on the
			     graphite it was rendered on, beside and behind the words. -->
			<div class="ap-stage">
				<img
					src="/img/{image.asset}.webp"
					alt={image.alt}
					width={image.w}
					height={image.h}
					fetchpriority="high"
					decoding="async"
				/>
			</div>
			<Reveal class="ap-hero__copy">
				<p class="ap-kicker"><span class="ap-kicker__tick" aria-hidden="true"></span>{label}</p>
				<h1>{title}</h1>
				<p class="ap-lede">{lede}</p>
			</Reveal>
		</div>
		<div class="ap-routes">
			{@render routes()}
			<p class="ap-updated">
				{image.caption}&nbsp;· facts checked <time datetime={updated}>{updated}</time>
			</p>
		</div>
	</Section>

	{@render children()}

	<Section tone="dark" grid class="ap-end">
		<Reveal>
			<p class="ap-kicker"><span class="ap-kicker__tick" aria-hidden="true"></span>Next step</p>
			<h2>See it run on a job like yours.</h2>
			<p class="ap-end__intro">{demo.intro}</p>
			<div class="ap-actions">
				<Button href="/ppf-pattern-software#demo" size="lg">{demo.cta}</Button>
				<Button href="/pricing" variant="dark" size="lg">{pricingCard.cta}</Button>
			</div>
			<p class="ap-updated">{pricingCard.refundLine} {pricingCard.deliveryLine}</p>
		</Reveal>
	</Section>
</main>
<Footer />

<style>
	:global(.ap-hero) {
		padding-block: clamp(5rem, 11vh, 7.5rem) 0;
	}
	.ap-scene {
		position: relative;
		display: flex;
		align-items: center;
		min-height: clamp(24rem, 56vh, 36rem);
	}
	.ap-scene :global(.ap-hero__copy) {
		position: relative;
		z-index: 1;
		max-width: 36rem;
	}
	.ap-stage {
		position: absolute;
		inset: 0 -2% 0 42%;
		animation: ap-stage-in 900ms var(--ease-out) both;
	}
	.ap-stage::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 1;
		background: linear-gradient(90deg, var(--color-app-950) 0%, transparent 36%);
		pointer-events: none;
	}
	.ap-stage img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}
	@keyframes ap-stage-in {
		from {
			opacity: 0;
			transform: translateX(12px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}
	.ap-routes {
		margin-top: clamp(2rem, 5vh, 3.5rem);
	}
	.ap-routes .ap-updated {
		max-width: none;
		margin-top: 0;
		padding-block: 1rem 0;
		border-top: 1px solid var(--color-app-700);
	}
	@media (max-width: 1023px) {
		.ap-scene {
			flex-direction: column;
			align-items: stretch;
			gap: 2rem;
			min-height: 0;
		}
		.ap-stage {
			position: relative;
			inset: auto;
			order: -1;
		}
		.ap-stage::before {
			display: none;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.ap-stage {
			animation: none;
		}
	}
	.ap-kicker {
		display: flex;
		align-items: center;
		gap: 0.625rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.ap-kicker__tick {
		width: 6px;
		height: 6px;
		background-color: var(--color-cut-400);
	}
	h1 {
		margin-top: 1.25rem;
		max-width: 16ch;
		font-size: clamp(2.5rem, 4.6vw, 4.25rem);
		font-weight: 600;
		line-height: 1;
		letter-spacing: -0.035em;
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	.ap-lede,
	.ap-end__intro {
		margin-top: 1.5rem;
		max-width: 46ch;
		font-size: var(--text-lead);
		line-height: 1.5;
		letter-spacing: -0.01em;
		color: var(--color-bone-300);
		text-wrap: pretty;
	}
	.ap-updated {
		margin-top: 1.75rem;
		max-width: 60ch;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-bone-500);
	}
	h2 {
		margin-top: 1.25rem;
		max-width: 18ch;
		font-size: var(--text-h2);
		font-weight: 600;
		line-height: var(--text-h2--line-height);
		letter-spacing: var(--text-h2--letter-spacing);
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	.ap-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 2rem;
	}
</style>
